import React from 'react';
import styled from 'styled-components';
import { Bio } from '../../data/constants';
import Typewriter from 'typewriter-effect';
import HeroImg from '../../images/HeroImage.jpg';
import HeroBgAnimation from '../HeroBgAnimation';
import { Tilt } from 'react-tilt';
import { motion } from 'framer-motion';
import {
  headContainerAnimation,
  headContentAnimation,
  headTextAnimation,
} from '../../utils/motion';

const HeroContainer = styled.div`
  background: linear-gradient(
    343deg,
    rgba(11, 14, 20, 1) 0%,
    rgba(21, 24, 33, 1) 50%,
    rgba(11, 14, 20, 1) 100%
  );
  display: flex;
  justify-content: center;
  position: relative;
  padding: 80px 30px;
  z-index: 1;

  @media (max-width: 960px) {
    padding: 66px 16px;
  }

  @media (max-width: 640px) {
    padding: 32px 16px;
  }

  clip-path: polygon(0 0, 100% 0, 100% 100%, 70% 95%, 0 100%);
`;
const HeroInnerContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 1100px;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;
const HeroLeftContainer = styled.div`
  width: 100%;
  order: 1;
  @media (max-width: 960px) {
    order: 2;
    margin-bottom: 30px;
    display: flex;
    gap: 6px;
    flex-direction: column;
    align-items: center;
  }
`;
const HeroRightContainer = styled.div`
  width: 100%;
  order: 2;
  display: flex;
  justify-content: end;
  @media (max-width: 960px) {
    order: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-contents: center;
    margin-bottom: 80px;
  }

  @media (max-width: 640px) {
    margin-bottom: 30px;
  }
`;

const Title = styled.h1`
  font-weight: 700;
  font-size: 50px;
  color: ${({ theme }) => theme.text_primary};
  line-height: 68px;
  margin: 0;

  @media (max-width: 1000px) {
    text-align: center;
  }

  @media (max-width: 1000px) {
    font-size: 40px;
    line-height: 48px;
    margin-bottom: 8px;
  }
`;

const TextLoop = styled.div`
  font-weight: 600;
  font-size: 26px;
  display: flex;
  gap: 10px;
  color: ${({ theme }) => theme.text_primary};
  line-height: 68px;

  @media (max-width: 960px) {
    text-align: center;
  }

  @media (max-width: 960px) {
    font-size: 22px;
    line-height: 48px;
    margin-bottom: 16px;
  }
`;

const Span = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme.primary};
  position: relative;
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: ${({ theme }) => theme.primary};
    transform: scaleX(0);
    transform-origin: bottom right;
    transition: transform 0.5s;
  }

  &:hover:after {
    transform-origin: bottom left;
    transform: scaleX(1);
  }
`;

const SubTitle = styled.div`
  font-size: 20px;
  line-height: 32px;
  margin-bottom: 42px;
  color: ${({ theme }) => theme.text_primary + 95};
  max-width: 650px;
  position: relative;
  padding-left: 20px;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    height: calc(100% - 20px);
    width: 4px;
    background: ${({ theme }) => theme.primary};
    border-radius: 5px;
  }

  @media (max-width: 960px) {
    text-align: center;
    padding-left: 0;

    &:before {
      display: none;
    }
  }

  @media (max-width: 960px) {
    font-size: 16px;
    line-height: 32px;
  }
`;

const ResumeButton = styled.a`
  -webkit-appearance: button;
  -moz-appearance: button;
  appearance: button;
  text-decoration: none;

  width: 95%;
  max-width: 300px;
  text-align: center;
  padding: 16px 0;

  background: hsla(210, 100%, 50%, 1);
  background: linear-gradient(225deg, hsla(210, 100%, 50%, 1) 0%, hsla(230, 100%, 50%, 1) 100%);
  background: -moz-linear-gradient(
    225deg,
    hsla(210, 100%, 50%, 1) 0%,
    hsla(230, 100%, 50%, 1) 100%
  );
  background: -webkit-linear-gradient(
    225deg,
    hsla(210, 100%, 50%, 1) 0%,
    hsla(230, 100%, 50%, 1) 100%
  );
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.5);
  border-radius: 8px;
  font-weight: 600;
  font-size: 20px;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: 0.5s;
  }

  &:hover {
    transform: translateY(-5px);
    transition: all 0.4s ease-in-out;
    box-shadow: 0px 7px 15px rgba(0, 0, 0, 0.6);
  }

  &:hover:before {
    left: 100%;
  }

  @media (max-width: 640px) {
    padding: 12px 0;
    font-size: 18px;
  }
  color: white;
`;

const ImgContainer = styled(motion.div)`
  border-radius: 10px;
  border: 2px solid ${({ theme }) => theme.primary};
  padding: 8px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  position: relative;
  overflow: hidden;

  &:before {
    content: '$ ./tristan_profile';
    position: absolute;
    top: 10px;
    left: 10px;
    background: rgba(0, 0, 0, 0.7);
    color: ${({ theme }) => theme.primary};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 14px;
    font-family: 'Courier New', monospace;
    font-weight: bold;
    z-index: 2;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background:
      linear-gradient(to bottom, transparent 0%, rgba(0, 30, 60, 0.2) 100%),
      repeating-linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.1) 0px,
        rgba(0, 0, 0, 0.1) 1px,
        transparent 1px,
        transparent 2px
      );
    z-index: 1;
    pointer-events: none;
  }
`;

const Img = styled.img`
  border-radius: 5px;
  width: 100%;
  height: 100%;
  max-width: 400px;
  max-height: 400px;
  transition: all 0.5s ease;
  filter: contrast(1.1) saturate(1.1);

  @media (max-width: 640px) {
    max-width: 280px;
    max-height: 280px;
  }
`;

const HeroBg = styled.div`
  position: absolute;
  display: flex;
  justify-content: end;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  max-width: 1360px;
  overflow: hidden;
  padding: 0 30px;
  top: 50%;
  left: 50%;
  -webkit-transform: translateX(-50%) translateY(-50%);
  transform: translateX(-50%) translateY(-50%);

  @media (max-width: 960px) {
    justify-content: center;
    padding: 0 0px;
  }
`;

const BackendIcons = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.1;
  z-index: -1;
  pointer-events: none;
`;

const CodeSnippet = styled.div`
  position: absolute;
  font-family: 'Courier New', monospace;
  color: ${({ theme }) => theme.primary};
  font-size: 12px;
  opacity: 0.4;
  user-select: none;
`;

// Create a terminal-like element for the hero section
const TerminalElement = styled.div`
  position: absolute;
  top: -5px;
  right: -20px;
  width: 150px;
  background: rgba(0, 20, 40, 0.9);
  border: 1px solid ${({ theme }) => theme.primary};
  border-radius: 8px;
  padding: 8px;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: #63ff00;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transform: rotate(15deg);
  z-index: 5;

  &:before {
    content: '$ status';
    display: block;
    color: ${({ theme }) => theme.primary};
    border-bottom: 1px dashed #333;
    padding-bottom: 4px;
    margin-bottom: 4px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const BlinkingCursor = styled.span`
  animation: blink 1s infinite;
  font-weight: bold;

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const Hero = () => {
  return (
    <div id="About">
      <HeroContainer>
        <HeroBg>
          {/* <StarCanvas /> */}
          <HeroBgAnimation />
          <BackendIcons>
            <CodeSnippet style={{ top: '10%', left: '5%' }}>
              {'const server = express();'}
            </CodeSnippet>
            <CodeSnippet style={{ top: '20%', right: '10%' }}>
              {'async function connectDB() {'}
            </CodeSnippet>
            <CodeSnippet style={{ bottom: '15%', left: '15%' }}>{'app.listen(3000);'}</CodeSnippet>
            <CodeSnippet style={{ bottom: '30%', right: '5%' }}>
              {'router.get("/api/v1/users");'}
            </CodeSnippet>
          </BackendIcons>
        </HeroBg>

        <motion.div {...headContainerAnimation}>
          <HeroInnerContainer>
            <HeroLeftContainer>
              <motion.div {...headTextAnimation}>
                <Title>
                  Hello, I'm <br /> {Bio.name}
                </Title>
                <TextLoop>
                  <span>I specialize in</span>
                  <Span>
                    <Typewriter
                      options={{
                        strings: Bio.roles,
                        autoStart: true,
                        loop: true,
                      }}
                    />
                  </Span>
                </TextLoop>
              </motion.div>

              <motion.div {...headContentAnimation}>
                <SubTitle>{Bio.description}</SubTitle>
              </motion.div>

              <ResumeButton href={Bio.resume} target="_blank">
                View My Resume
              </ResumeButton>
            </HeroLeftContainer>
            <HeroRightContainer>
              <ImgContainer {...headContentAnimation}>
                <Tilt options={{ max: 25, scale: 1.05 }}>
                  <Img src={HeroImg} alt={Bio.name} />
                  <TerminalElement>
                    online: true
                    <br />
                    backend: ready
                    <br />
                    coding: active
                    <br />
                    <BlinkingCursor>_</BlinkingCursor>
                  </TerminalElement>
                </Tilt>
              </ImgContainer>
            </HeroRightContainer>
          </HeroInnerContainer>
        </motion.div>
      </HeroContainer>
    </div>
  );
};

export default Hero;
